import { Auth0Provider } from "@auth0/auth0-react";
import { ThemeProvider } from "@mui/material";
import { theme } from "@talkouttech/portal-shared";
import { ScrollToTop } from "components/ScrollToTop";
import { FullPageLoader } from "components/ui/FullPageLoader";
import i18n from "language/i18n";
import React, { FC } from "react";
import { I18nextProvider } from "react-i18next";
import { QueryClient, QueryClientProvider } from "react-query";
import { ReactQueryDevtools } from "react-query/devtools";
import { BrowserRouter as Router } from "react-router-dom";
import { AUTH0_SCOPE, AuthProvider } from "./auth-context";
import { ModalProvider } from "./modal-context";
import { ToastProvider } from "./toast-context";

const MILLISECONDS_IN_HOUR = 1000 * 60 * 60;

const queryConfig = new QueryClient({
  defaultOptions: {
    queries: {
      useErrorBoundary: true,
      refetchOnWindowFocus: false,
      staleTime: MILLISECONDS_IN_HOUR,
      cacheTime: MILLISECONDS_IN_HOUR
    }
  }
});

export const BasicProviders: FC = ({ children }) => (
  <React.Suspense fallback={<FullPageLoader />}>
    <QueryClientProvider client={queryConfig}>
      <Router>
        <ScrollToTop />
        <ThemeProvider theme={theme}>
          <ToastProvider>
            <ModalProvider>{children}</ModalProvider>
          </ToastProvider>
        </ThemeProvider>
      </Router>
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  </React.Suspense>
);

const AppProviders: FC = ({ children }) => (
  <I18nextProvider i18n={i18n}>
    <Auth0Provider
      domain={window.APP_CONFIG.AUTH0_DOMAIN}
      clientId={window.APP_CONFIG.AUTH0_CLIENTID}
      redirectUri={`${window.location.origin}/home`}
      audience={window.APP_CONFIG.AUTH0_AUDIENCE}
      useRefreshTokens
      cacheLocation="localstorage"
      onRedirectCallback={appState => {
        if (appState?.returnTo) {
          window.location.href = appState!.returnTo;
        }
      }}
      scope={AUTH0_SCOPE}
    >
      <AuthProvider>
        <BasicProviders>{children}</BasicProviders>
      </AuthProvider>
    </Auth0Provider>
  </I18nextProvider>
);

export { AppProviders };
